import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { useTranslationProjects } from '../hooks/useTranslationProjects';
import { Button } from '../components/common';
import { Book } from '../types';
import {
  getGraphQLClient,
  GET_PUBLISHED_BOOKS,
  GET_TRENDING_BOOKS,
  GET_MY_FAVORITE_BOOKS,
  GET_BOOKS,
} from '../lib/graphql';
import { BookCard } from '../components/books';
import { idFromLocation } from '../lib/lastLocation';

type TabType = 'trending' | 'recent' | 'favorites';

export default function HomePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [trendingBooks, setTrendingBooks] = useState<Book[]>([]);
  const [recentBooks, setRecentBooks] = useState<Book[]>([]);
  const [favoriteBooks, setFavoriteBooks] = useState<Book[]>([]);
  const [userBooks, setUserBooks] = useState<Book[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>('trending');
  const [isLoading, setIsLoading] = useState(true);

  // Translation projects still being worked on (not fully translated yet).
  const { data: translationProjects } = useTranslationProjects();
  const inProgressTranslations = (translationProjects ?? [])
    .filter((p) => p.translatedCount < p.segmentCount || p.status !== 'ready')
    .slice(0, 4);

  // "Jump back in": resolve the user's last meaningful location (recorded server-side,
  // so it follows them across devices) into a card with a real title + link.
  const jumpBack = useMemo(() => {
    const loc = user?.lastLocation;
    if (!loc) return null;
    const id = idFromLocation(loc.path);

    if (loc.kind === 'translation') {
      const project = (translationProjects ?? []).find((p) => p.id === id);
      return {
        path: loc.path,
        kind: 'translation' as const,
        title: project?.title || t('home.jumpBackTranslationFallback'),
        coverImageUrl: undefined as string | undefined,
        updatedAt: loc.updatedAt,
      };
    }

    const book = userBooks.find((b) => b.id === id);
    return {
      path: loc.path,
      kind: 'book' as const,
      title: book?.title || t('home.jumpBackBookFallback'),
      coverImageUrl: book?.coverImageUrl,
      updatedAt: loc.updatedAt,
    };
  }, [user?.lastLocation, translationProjects, userBooks, t]);

  useEffect(() => {
    const loadBooks = async () => {
      try {
        const client = getGraphQLClient();
        const [trendingData, recentData] = await Promise.all([
          client.request<{ trendingBooks: Book[] }>(GET_TRENDING_BOOKS, { limit: 8 }),
          client.request<{ publishedBooks: Book[] }>(GET_PUBLISHED_BOOKS, { limit: 8 }),
        ]);
        setTrendingBooks(trendingData.trendingBooks);
        setRecentBooks(recentData.publishedBooks);

        if (user) {
          const [favoritesData, booksData] = await Promise.all([
            client.request<{ myFavoriteBooks: Book[] }>(GET_MY_FAVORITE_BOOKS),
            client.request<{ books: Book[] }>(GET_BOOKS),
          ]);
          setFavoriteBooks(favoritesData.myFavoriteBooks);
          // Keep the full list (sorted by recency) so "jump back in" can resolve
          // any book by id; the Continue Writing grid slices to 4 at render.
          const sortedBooks = booksData.books.sort(
            (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );
          setUserBooks(sortedBooks);
        }
      } catch (error) {
        console.error('Failed to load books:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadBooks();
  }, [user]);

  const handleFavoriteToggle = (bookId: string, isFavorited: boolean) => {
    if (isFavorited) {
      const book = [...trendingBooks, ...recentBooks].find((b) => b.id === bookId);
      if (book && !favoriteBooks.find((b) => b.id === bookId)) {
        setFavoriteBooks((prev) => [{ ...book, isFavorited: true }, ...prev]);
      }
    } else {
      setFavoriteBooks((prev) => prev.filter((b) => b.id !== bookId));
    }
  };

  const getActiveBooks = () => {
    switch (activeTab) {
      case 'trending':
        return trendingBooks;
      case 'recent':
        return recentBooks;
      case 'favorites':
        return favoriteBooks;
      default:
        return trendingBooks;
    }
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('home.justNow');
    if (diffMins < 60) return t('home.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('home.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('home.daysAgo', { count: diffDays });
    return date.toLocaleDateString();
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Subtle background gradients */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/3 right-0 w-72 h-72 bg-accent-500/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section - More compact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4">
            <span className="text-gradient bg-300% animate-gradient inline-block">
              {t('app.name')}
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-8">
            {t('home.heroSubtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {user ? (
              <>
                <Link to="/books/new">
                  <Button className="btn-primary btn-lg text-base px-8 py-4 shadow-lg hover:shadow-primary-500/30">
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    {t('home.startWriting')}
                  </Button>
                </Link>
                <Link to="/books">
                  <Button variant="secondary" size="lg" className="text-base px-8 py-4">
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    </svg>
                    {t('book.myBooks')}
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/register">
                  <Button className="btn-primary btn-lg text-base px-8 py-4 shadow-lg hover:shadow-primary-500/30">
                    {t('home.getStartedFree')}
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="secondary" size="lg" className="text-base px-8 py-4">
                    {t('home.signIn')}
                  </Button>
                </Link>
              </>
            )}
          </div>
        </motion.div>

        {/* Jump back in - resume the user's last book/translation across devices */}
        {user && jumpBack && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <Link
              to={jumpBack.path}
              className="group flex items-center gap-4 p-5 rounded-2xl border border-primary-200 dark:border-primary-800 bg-gradient-to-r from-primary-50 to-accent-50 dark:from-primary-900/30 dark:to-accent-900/20 hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-600 transition-all"
            >
              {jumpBack.coverImageUrl ? (
                <img
                  src={jumpBack.coverImageUrl}
                  alt={jumpBack.title}
                  className="w-14 h-20 object-cover rounded-lg flex-shrink-0 shadow-sm"
                />
              ) : (
                <div className="w-14 h-20 rounded-lg flex-shrink-0 bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-800 dark:to-primary-700 flex items-center justify-center">
                  <svg
                    className="w-7 h-7 text-primary-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={
                        jumpBack.kind === 'translation'
                          ? 'M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129'
                          : 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253'
                      }
                    />
                  </svg>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400">
                  {jumpBack.kind === 'translation'
                    ? t('home.jumpBackTranslating')
                    : t('home.jumpBackWriting')}
                </p>
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate mt-0.5">
                  {jumpBack.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {formatRelativeTime(jumpBack.updatedAt)}
                </p>
              </div>
              <span className="flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 flex-shrink-0">
                {t('home.jumpBackResume')}
                <svg
                  className="w-4 h-4 transition-transform group-hover:translate-x-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </span>
            </Link>
          </motion.div>
        )}

        {/* Continue Writing Section - For authenticated users with books */}
        {user && userBooks.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-16"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {t('home.continueWriting')}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                  {t('home.continueWritingSubtitle')}
                </p>
              </div>
              <Link to="/books">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-primary-600 dark:text-primary-400"
                >
                  {t('home.viewAllDrafts')}
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {userBooks.slice(0, 4).map((book, index) => (
                <motion.div
                  key={book.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.05 }}
                >
                  <Link
                    to={`/books/${book.id}`}
                    className="block p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start gap-3">
                      {book.coverImageUrl ? (
                        <img
                          src={book.coverImageUrl}
                          alt={book.title ?? undefined}
                          className="w-12 h-16 object-cover rounded-lg flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-16 bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900 dark:to-primary-800 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg
                            className="w-6 h-6 text-primary-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                            />
                          </svg>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                          {book.title || t('books.untitledBook')}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {t(`bookTypes.${book.bookType?.toLowerCase()}`)}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          {formatRelativeTime(book.updatedAt)}
                        </p>
                      </div>
                      <svg
                        className="w-5 h-5 text-gray-400 group-hover:text-primary-500 transition-colors flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Continue Translating Section - For authenticated users with in-progress translations */}
        {user && inProgressTranslations.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-16"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {t('home.continueTranslating')}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                  {t('home.continueTranslatingSubtitle')}
                </p>
              </div>
              <Link to="/books?tab=translations">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-primary-600 dark:text-primary-400"
                >
                  {t('home.viewAllTranslations')}
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {inProgressTranslations.map((project, index) => {
                const pct =
                  project.segmentCount > 0
                    ? Math.round((project.translatedCount / project.segmentCount) * 100)
                    : 0;
                return (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 + index * 0.05 }}
                  >
                    <Link
                      to={`/translate/${project.id}`}
                      className="block p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md transition-all group"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-16 bg-gradient-to-br from-accent-100 to-accent-200 dark:from-accent-900 dark:to-accent-800 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg
                            className="w-6 h-6 text-accent-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                            />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                            {project.title}
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {project.sourceLanguage} → {project.targetLanguage}
                            {project.status !== 'ready' && (
                              <em className="ml-1">
                                ({t(`statuses.${project.status}`, project.status)})
                              </em>
                            )}
                          </p>
                          <div className="mt-2 flex items-center gap-2">
                            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                              <div className="h-full bg-primary-500" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                              {pct}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* How It Works - For non-authenticated or users without books */}
        {(!user || userBooks.length === 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 text-center mb-8">
              {t('home.howItWorks')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                {
                  step: 1,
                  icon: (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  ),
                  title: t('home.step1Title'),
                  description: t('home.step1Description'),
                },
                {
                  step: 2,
                  icon: (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                    />
                  ),
                  title: t('home.step2Title'),
                  description: t('home.step2Description'),
                },
                {
                  step: 3,
                  icon: (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  ),
                  title: t('home.step3Title'),
                  description: t('home.step3Description'),
                },
                {
                  step: 4,
                  icon: (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  ),
                  title: t('home.step4Title'),
                  description: t('home.step4Description'),
                },
              ].map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center text-white shadow-lg">
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      {item.icon}
                    </svg>
                  </div>
                  <div className="text-xs font-medium text-primary-600 dark:text-primary-400 mb-2">
                    {t('home.stepLabel', { step: item.step })}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Books Gallery Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-16"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {t('home.discoverBooks')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">{t('home.discoverBooksSubtitle')}</p>
          </div>

          {/* Tabs */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex max-w-full overflow-x-auto bg-gray-100 dark:bg-gray-800 rounded-xl p-1 gap-1">
              <button
                onClick={() => setActiveTab('trending')}
                className={`px-3 sm:px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex flex-shrink-0 items-center gap-2 whitespace-nowrap ${
                  activeTab === 'trending'
                    ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
                {t('home.trending')}
              </button>
              <button
                onClick={() => setActiveTab('recent')}
                className={`px-3 sm:px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex flex-shrink-0 items-center gap-2 whitespace-nowrap ${
                  activeTab === 'recent'
                    ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {t('home.recent')}
              </button>
              {user && (
                <button
                  onClick={() => setActiveTab('favorites')}
                  className={`px-3 sm:px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex flex-shrink-0 items-center gap-2 whitespace-nowrap ${
                    activeTab === 'favorites'
                      ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                  </svg>
                  {t('home.myFavorites')}
                  {favoriteBooks.length > 0 && (
                    <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                      {favoriteBooks.length}
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Books Grid */}
          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex justify-center items-center py-16"
              >
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
              </motion.div>
            ) : getActiveBooks().length > 0 ? (
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
              >
                {getActiveBooks().map((book, index) => (
                  <motion.div
                    key={book.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                  >
                    <BookCard
                      book={book}
                      variant="gallery"
                      showStats={true}
                      showFavoriteButton={true}
                      onFavoriteToggle={handleFavoriteToggle}
                    />
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-16"
              >
                <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                  {activeTab === 'favorites' ? (
                    <svg
                      className="w-10 h-10 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-10 h-10 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    </svg>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {activeTab === 'favorites' ? t('home.noFavoritesYet') : t('home.noBooksFound')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
                  {activeTab === 'favorites' ? t('home.startExploring') : t('home.beTheFirst')}
                </p>
                {activeTab === 'favorites' && (
                  <Button onClick={() => setActiveTab('trending')} variant="primary" size="sm">
                    {t('home.exploreTrendingBooks')}
                  </Button>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* View All Link */}
          {getActiveBooks().length > 0 && (
            <div className="text-center mt-8">
              <Link to="/browse">
                <Button variant="secondary" className="group">
                  <span>{t('home.viewAllBooks')}</span>
                  <svg
                    className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </Button>
              </Link>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
